// app/javascript/controllers/featured_carousel_controller.js
import { Controller } from "@hotwired/stimulus"

const STATE_CLASSES = {
  // bump center only ≥sm
  current:  "z-30 sm:scale-[1.10] opacity-100",
  // side overlap only ≥sm to avoid mobile collisions
  previous: "z-20 sm:-ml-[285px] sm:opacity-90",
  next:     "z-20 sm:ml-[285px]  sm:opacity-90",
  // hide non-adjacent only ≥sm; keep all visible on mobile for scroll list
  hidden:   "sm:z-10 sm:opacity-0 sm:pointer-events-none sm:scale-95"
}

export default class extends Controller {
  static targets = ["slide","name","index","total","previousButton","nextButton","scroller"]
  static values  = {
    index: { type: Number, default: 0 },
    categoriesSectionId: { type: String, default: "parts-categories" }
  }

  connect() {
    this.total = this.slideTargets.length
    if (!this.total) return

    // cache base classes
    this.slideTargets.forEach(el => { el.dataset.baseClass ||= el.className })

    // clamp index
    if (this.indexValue >= this.total) this.indexValue = 0

    // counts
    if (this.hasTotalTarget) this.totalTarget.textContent = this._fmt(this.total)

    // nav state
    if (this.total <= 1) this._disableNav()

    // media query to detect mobile
    this._mq = window.matchMedia("(max-width: 639px)")

    // categories section + current url cache
    this._categoriesSection = this._findCategoriesSection()
    const initialSlide = this.slideTargets[this.indexValue]
    this._currentCategoriesUrl = initialSlide?.dataset.categoriesUrl || null
    this._pendingCategoriesUrl = null
    this._categoriesAbortController = null

    this.update()
    this._ensureVisible()
  }

  disconnect() {
    this._abortCategoriesRequest()
  }

  next(e){ e?.preventDefault(); if (this.total > 1) this._goto((this.indexValue + 1) % this.total) }
  previous(e){ e?.preventDefault(); if (this.total > 1) this._goto((this.indexValue - 1 + this.total) % this.total) }

  selectSlide(event){
    event.preventDefault()
    const index = Number(event.params.index)
    if (Number.isNaN(index)) return
    this._goto(index)
    this.scrollToCategories()
  }

  scrollToCategories(){
    const section = this._categoriesSection || this._findCategoriesSection()
    if (!section) return
    section.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  _goto(i){
    if (i === this.indexValue) return
    this.indexValue = i
    this.update()
    this._ensureVisible()
  }

  update() {
    const n = this.total
    const c = this.indexValue
    const l = (c - 1 + n) % n
    const r = (c + 1) % n

    this.slideTargets.forEach((el, i) => {
      const base = el.dataset.baseClass || ""
      let key = "hidden"
      if (i === c) key = "current"
      else if (n === 2 && i !== c) key = "next"
      else if (i === l) key = "previous"
      else if (i === r) key = "next"
      el.className = `${base} ${STATE_CLASSES[key]}`
    })

    if (this.hasNameTarget)  this.nameTarget.textContent  = this.slideTargets[c]?.dataset.name || ""
    if (this.hasIndexTarget) this.indexTarget.textContent = this._fmt(c + 1)

    this._updateCategories()
  }

  _ensureVisible(){
    if (!this.hasScrollerTarget) return
    // On mobile, scroll the strip so active slide is centered.
    if (this._mq?.matches) {
      const el = this.slideTargets[this.indexValue]
      if (!el) return
      const scroller = this.scrollerTarget
      const left = el.offsetLeft - (scroller.clientWidth - el.clientWidth) / 2
      scroller.scrollTo({ left, top: 0, behavior: "smooth" })
    }
  }

  _disableNav(){
    this.previousButtonTarget?.setAttribute("disabled","disabled")
    this.previousButtonTarget?.classList.add("pointer-events-none","opacity-40")
    this.nextButtonTarget?.setAttribute("disabled","disabled")
    this.nextButtonTarget?.classList.add("pointer-events-none","opacity-40")
  }

  _fmt(x){ return String(x).padStart(2,"0") }

  _findCategoriesSection(){
    const id = this.hasCategoriesSectionIdValue ? this.categoriesSectionIdValue : "parts-categories"
    return id ? document.getElementById(id) : null
  }

  _updateCategories(){
    const slide = this.slideTargets[this.indexValue]
    if (!slide) return

    const url = slide.dataset.categoriesUrl
    if (!url || url === this._currentCategoriesUrl || url === this._pendingCategoriesUrl) return

    const section = this._categoriesSection || this._findCategoriesSection()
    if (!section) return

    const sectionId = section.id || this.categoriesSectionIdValue || "parts-categories"
    section.setAttribute("aria-busy","true")
    section.classList.add("opacity-50")

    this._abortCategoriesRequest()
    const abortController = new AbortController()
    this._categoriesAbortController = abortController
    this._pendingCategoriesUrl = url

    fetch(url, {
      headers: { Accept: "text/vnd.turbo-stream.html, text/html" },
      signal: abortController.signal
    })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.text()
      })
      .then(html => {
        if (abortController.signal.aborted) return
        const template = document.createElement("template")
        template.innerHTML = html.trim()
        const newSection = template.content.firstElementChild
        if (!newSection) throw new Error("Categories partial response missing root element")
        section.replaceWith(newSection)
        this._categoriesSection = newSection
        this._currentCategoriesUrl = url
      })
      .catch(error => {
        if (error.name !== "AbortError") {
          console.error("[featured-carousel] Failed to load categories:", error)
        }
      })
      .finally(() => {
        if (abortController.signal.aborted) return
        this._pendingCategoriesUrl = null
        this._categoriesAbortController = null
        const currentSection = this._categoriesSection || document.getElementById(sectionId)
        currentSection?.classList.remove("opacity-50")
        currentSection?.removeAttribute("aria-busy")
      })
  }

  _abortCategoriesRequest(){
    if (this._categoriesAbortController) {
      this._categoriesAbortController.abort()
      this._categoriesAbortController = null
    }
  }
}
